'use es6';

import { connect } from 'react-redux';
import debounce from 'react-utils/debounce';
import { useRef, useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import I18n from 'I18n';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { Section } from 'foundations-components/transitional/components/Section';
import { Flex } from 'foundations-components/components/Flex';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { fetchSavedSections } from 'LayoutBuilderUI/actions/ApiActions';
import LoadingTile from 'LayoutBuilderUI/components/tiles/LoadingTile';
import ManageSectionsButton from 'LayoutBuilderUI/components/ManageSectionsButton';
import SectionTile from 'LayoutBuilderUI/components/tiles/SectionTile';
import ExpandableSectionContentWrapper from 'LayoutBuilderUI/components/panels/sidebar/reusableHelpers/ExpandableSectionContentWrapper';
import { getTooltipPlacement } from 'LayoutBuilderUI/components/panels/sidebar/utils/tooltips';
import AddPanelExpandableSection from 'LayoutBuilderUI/components/panels/sidebar/reusableHelpers/AddPanelExpandableSections';
import { getIsReadOnlyMode } from 'EmailEditorUI/redux/selectors/ReadOnlySelector';
import { getSavedSectionsOrdered, getShowLoadingStateForAllSections, getShowLoadingStateForSavingSection, getSavedSectionsTotal } from 'EmailEditorUI/redux/selectors/savedSectionSelectors';
import { SECTION_LIMIT } from 'EmailEditorUI/constants/SidebarConstants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
function SavedSectionsPanelSection({
  disabled,
  sections,
  savedSectionsTotal,
  isSavingSection,
  sectionsLoading,
  searchQuery = ''
}) {
  const [didSave, setDidSave] = useState(false);
  const [searchQueryState, setSearchQueryState] = useState('');
  const [allSavedSectionsFetched, setAllSavedSectionsFetched] = useState(false);
  const dropArea = useRef(null);
  const debouncedSetSearchQuery = useRef(debounce(search => {
    fetchSavedSections(false, true, {
      name: search,
      limit: 50
    });
  }, 300)).current;
  useEffect(() => {
    if (isSavingSection) {
      setDidSave(true);
    }
    if ((didSave || isSavingSection) && dropArea.current) {
      dropArea.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
      setDidSave(false);
    }
  }, [isSavingSection, didSave]);
  const onChangeSearch = event => {
    const search = event.target.value;
    setSearchQueryState(search);
    if (!allSavedSectionsFetched) {
      debouncedSetSearchQuery(search);
    }
  };
  const renderEmptyState = () => {
    if (sectionsLoading || !searchQuery || sections.length > 0) {
      return null;
    }
    return /*#__PURE__*/_jsx(ErrorMessage, {
      illustration: "empty-state-charts",
      illustrationProps: {
        inlineSize: '100px'
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "layoutBuilder.addPanel.savedSections.emptyState",
        options: {
          searchQuery
        }
      })
    });
  };
  const filteredSections = sections.filter(s => searchQueryState && allSavedSectionsFetched ? s.label.toLowerCase().includes(searchQueryState) : true);
  const shouldRenderShowMore = savedSectionsTotal > SECTION_LIMIT && !allSavedSectionsFetched;
  const savedSections = /*#__PURE__*/_jsx(Section, {
    position: "relative",
    children: /*#__PURE__*/_jsxs(Flex, {
      "data-test-id": "saved-sections-section",
      flexWrap: "wrap",
      gap: "200",
      children: [renderEmptyState(), sectionsLoading && /*#__PURE__*/_jsx(LoadingOverlay, {
        contextual: true
      }), isSavingSection && /*#__PURE__*/_jsx(LoadingTile, {}), filteredSections.map((s, index) => {
        return /*#__PURE__*/_jsx(SectionTile, {
          "data-test-id": s.label,
          savedSection: s,
          disabled: disabled,
          tooltipPlacement: getTooltipPlacement(index)
        }, s.id);
      })]
    })
  });
  return /*#__PURE__*/_jsx(AddPanelExpandableSection, {
    name: "saved-sections-section",
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "layoutBuilder.addPanel.savedSections.heading"
    }),
    actions: /*#__PURE__*/_jsx(ManageSectionsButton, {
      disabled: disabled || !sections.length
    }),
    removeRightPaddingFromContent: true,
    indentation: "compact",
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(FormControl, {
        marginInlineEnd: "500",
        marginBlockEnd: "200",
        "aria-label": "Search",
        children: /*#__PURE__*/_jsx(SearchInput, {
          defaultValue: searchQueryState,
          onChange: onChangeSearch,
          placeholder: I18n.text(`layoutBuilder.addPanel.savedSections.search`)
        })
      }), /*#__PURE__*/_jsx(ExpandableSectionContentWrapper, {
        numberOfTiles: filteredSections.length,
        tilesPerRow: 2,
        shouldRenderShowMore: shouldRenderShowMore,
        onShowMoreClick: () => {
          setAllSavedSectionsFetched(true);
          fetchSavedSections(true);
        },
        children: savedSections
      })]
    })
  });
}
const mapStateToProps = (state, {
  searchQuery = ''
}) => {
  let sections = getSavedSectionsOrdered(state);
  if (searchQuery) {
    sections = sections.filter(s => s.label.toLowerCase().includes(searchQuery));
  }
  const sectionsLoading = getShowLoadingStateForAllSections(state);
  return {
    disabled: getIsReadOnlyMode(state),
    sections,
    savedSectionsTotal: getSavedSectionsTotal(state),
    sectionsLoading,
    isSavingSection: getShowLoadingStateForSavingSection(state)
  };
};
export default connect(mapStateToProps)(SavedSectionsPanelSection);